import {
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button,
  FormControl,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useState } from 'react';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useDeleteOrganizationMutation } from '@/state/mutations/organizations/use-delete-organization';
import { useNotifications } from '@/hooks/use-notifications';
import { useNavigate } from 'react-router';

type DeletionStep = 'initial' | 'confirm';

export default function DeleteOrganization({ close }: { close: React.Dispatch<React.SetStateAction<void>> }) {
  const t = useTranslations();
  const notification = useNotifications();
  const navigate = useNavigate();
  const { activeOrganization, setActiveOrganization } = useActiveOrganizationStore();
  const { mutateAsync: deleteOrganizationMutation } = useDeleteOrganizationMutation();

  const [deletionStep, setDeletionStep] = useState<DeletionStep>('initial');
  const [verifyOrganizationName, setVerifyOrganizationName] = useState<string>('');

  const deleteNameMatches = activeOrganization?.name === verifyOrganizationName;

  const handleDeleteOrganization = useCallback(async () => {
    if (!activeOrganization?.id) return;

    if (deletionStep === 'initial') {
      setDeletionStep('confirm');
    } else {
      if (deletionStep === 'confirm' && deleteNameMatches) {
        try {
          await deleteOrganizationMutation(activeOrganization.id);
          notification.notifySuccess(t('organization.settingsMenu.delete.success'));
          setActiveOrganization(undefined);
          close();
          navigate('/');
        } catch {
          notification.notifyError(t('organization.settingsMenu.delete.error'));
        }
      }
    }
  }, [
    deleteNameMatches,
    activeOrganization,
    deletionStep,
    deleteOrganizationMutation,
    notification,
    t,
    setActiveOrganization,
    close,
    navigate,
  ]);

  const verifyOrganizationNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerifyOrganizationName(event.target.value);
  };

  const accordionSummaryStyles = {
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
  };

  return (
    <Accordion sx={{ backgroundColor: 'transparent' }} data-testid="delete-organization-accordion">
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
        <Typography>{t('organization.settingsMenu.delete.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ py: 0 }}>
        <Stack spacing={1}>
          <Typography variant="body2">{t('organization.settingsMenu.delete.text')}</Typography>
          {deletionStep === 'confirm' && (
            <FormControl variant="standard">
              <Typography variant="caption">{t('organization.settingsMenu.delete.typeToConfirmLabel')}</Typography>
              <TextField
                value={verifyOrganizationName}
                onChange={verifyOrganizationNameChangeHandler}
                variant="standard"
                type="text"
                sx={{ maxWidth: 200 }}
                autoComplete="off"
                slotProps={{ htmlInput: { 'data-testid': 'delete-organization-verification-input' } }}
              />
            </FormControl>
          )}
          <Stack alignItems="flex-start">
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleDeleteOrganization}
              disabled={deletionStep === 'confirm' && !deleteNameMatches}
              data-testid="delete-organization-button"
            >
              {t('common.delete')}
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
